import type { AudioJitProviderId } from './audioJitCacheKey';
import { normalizeJitCacheText } from './audioJitCacheKey';
import {
  AUDIO_JIT_LEXICON_BASE_URL,
  AUDIO_JIT_ENV,
  AUDIO_JIT_BUCKET
} from './audioJitEnv';
import {
  getManifestRevision,
  normalizeLexiconManifest,
  type LexiconRevisionsManifest
} from './audioJitLexiconManifest';

export { AUDIO_JIT_LEXICON_BASE_URL, AUDIO_JIT_ENV, AUDIO_JIT_BUCKET };
export type { LexiconRevisionsManifest };

export const LEXICON_REVISIONS_URL = `${AUDIO_JIT_LEXICON_BASE_URL}/customlexicon-revisions.json`;

export const lexiconXmlUrl = (voiceLocale: string) =>
  `${AUDIO_JIT_LEXICON_BASE_URL}/customlexicon_${voiceLocale}.xml`;

export const googleLexiconJsonUrl = (voiceLocale: string) =>
  `${AUDIO_JIT_LEXICON_BASE_URL}/customlexicon_google_${voiceLocale}.json`;

/** In-memory manifest TTL — kept short so client stays near server after publish. */
const LEXICON_MANIFEST_TTL_MS = 60_000;
const LEXICON_ENTRIES_TTL_MS = 600_000;

export type LexiconEntry =
  | { type: 'alias'; match: string; alias: string }
  | { type: 'phoneme'; match: string; phoneme: string };

type GoogleLexiconRawEntry = {
  match?: string | string[];
  alias?: string;
  phoneme?: string;
};

type GoogleLexiconFile = {
  lexicon?: {
    revision?: string | number;
    entries?: GoogleLexiconRawEntry[];
  };
};

let manifestCache: {
  data: LexiconRevisionsManifest;
  expiresAt: number;
} | null = null;

let manifestInFlight: Promise<LexiconRevisionsManifest> | null = null;

const azureEntriesCache = new Map<
  string,
  { entries: LexiconEntry[]; expiresAt: number }
>();

const googleEntriesCache = new Map<
  string,
  { entries: LexiconEntry[]; expiresAt: number }
>();

const regexEscape = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// Case-sensitive: lexicon entries target specific casing (the proper noun
// `MoDel`, the acronym `ACT`), so a case-insensitive match would rewrite the
// ordinary words "model"/"act". Must stay in lockstep with the backend
// buildLexiconRegex (lexiconMatch.js) or runtime/server cache keys diverge.
const buildLexiconRegex = (match: string) =>
  new RegExp(`(?<!\\w)${regexEscape(match)}(?!\\w)`, 'g');

const normalizeMatchValues = (
  match: string | string[] | undefined
): string[] => {
  if (typeof match === 'string' && match.trim()) {
    return [match.trim()];
  }
  if (Array.isArray(match)) {
    const values = match
      .filter(value => typeof value === 'string' && value.trim())
      .map(value => value.trim());
    return [...new Set(values)];
  }
  return [];
};

const parseRawGoogleEntry = (
  rawEntry: GoogleLexiconRawEntry
): LexiconEntry[] => {
  const matches = normalizeMatchValues(rawEntry.match);
  if (!matches.length) return [];

  const alias = typeof rawEntry.alias === 'string' ? rawEntry.alias.trim() : '';
  const phoneme =
    typeof rawEntry.phoneme === 'string' ? rawEntry.phoneme.trim() : '';

  if (Boolean(alias) === Boolean(phoneme)) {
    return [];
  }

  return matches.map(match =>
    alias
      ? { type: 'alias' as const, match, alias }
      : { type: 'phoneme' as const, match, phoneme }
  );
};

export function parseGoogleLexiconJson(raw: unknown): {
  revision: string | null;
  entries: LexiconEntry[];
} {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { revision: null, entries: [] };
  }

  const lexicon = (raw as GoogleLexiconFile).lexicon;
  if (!lexicon || typeof lexicon !== 'object') {
    return { revision: null, entries: [] };
  }

  const revisionValue = lexicon.revision;
  const revision =
    revisionValue != null && String(revisionValue).trim() !== ''
      ? String(revisionValue).trim()
      : null;

  if (!revision || !Array.isArray(lexicon.entries)) {
    return { revision: null, entries: [] };
  }

  const entries = lexicon.entries.flatMap(parseRawGoogleEntry);
  return { revision, entries };
}

export function parsePlsLexicon(xml: string): LexiconEntry[] {
  if (!xml) return [];
  const entries: LexiconEntry[] = [];
  const lexemeBlocks = xml.match(/<lexeme\b[^>]*>[\s\S]*?<\/lexeme>/gi) || [];

  for (const block of lexemeBlocks) {
    const graphemes = [
      ...block.matchAll(/<grapheme>([^<]*)<\/grapheme>/gi)
    ].map(m => m[1]);
    if (!graphemes.length) continue;

    const aliasMatch = block.match(/<alias>([^<]*)<\/alias>/i);
    const phonemeMatch = block.match(/<phoneme>([^<]*)<\/phoneme>/i);

    if (aliasMatch) {
      const alias = aliasMatch[1].trim();
      for (const match of graphemes) {
        entries.push({ type: 'alias', match, alias });
      }
    } else if (phonemeMatch) {
      const phoneme = phonemeMatch[1].trim();
      for (const match of graphemes) {
        entries.push({ type: 'phoneme', match, phoneme });
      }
    }
  }

  return entries;
}

/** Clear in-memory lexicon caches (e.g. after cache-key mismatch retry). */
export function clearLexiconCaches(): void {
  manifestCache = null;
  manifestInFlight = null;
  azureEntriesCache.clear();
  googleEntriesCache.clear();
}

async function fetchLexiconRevisionsManifest(): Promise<LexiconRevisionsManifest> {
  if (manifestCache && manifestCache.expiresAt > Date.now()) {
    return manifestCache.data;
  }

  if (!manifestInFlight) {
    manifestInFlight = (async () => {
      try {
        const response = await fetch(LEXICON_REVISIONS_URL, {
          cache: 'no-cache'
        });
        if (!response.ok) {
          return manifestCache?.data ?? normalizeLexiconManifest(null);
        }
        const raw = (await response.json()) as unknown;
        const data = normalizeLexiconManifest(raw);
        manifestCache = {
          data,
          expiresAt: Date.now() + LEXICON_MANIFEST_TTL_MS
        };
        return data;
      } catch {
        return manifestCache?.data ?? normalizeLexiconManifest(null);
      } finally {
        manifestInFlight = null;
      }
    })();
  }

  return manifestInFlight;
}

async function loadAzureLexiconEntries(
  voiceLocale: string,
  revision: string
): Promise<LexiconEntry[]> {
  const cacheKey = `azure:${voiceLocale}:${revision}`;
  const cached = azureEntriesCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.entries;
  }

  try {
    const response = await fetch(lexiconXmlUrl(voiceLocale), {
      cache: 'no-cache'
    });
    if (!response.ok) {
      return [];
    }
    const xml = await response.text();
    const entries = parsePlsLexicon(xml);
    azureEntriesCache.set(cacheKey, {
      entries,
      expiresAt: Date.now() + LEXICON_ENTRIES_TTL_MS
    });
    return entries;
  } catch {
    return cached?.entries ?? [];
  }
}

async function loadGoogleLexiconEntries(
  voiceLocale: string,
  revision: string
): Promise<LexiconEntry[]> {
  const cacheKey = `google:${voiceLocale}:${revision}`;
  const cached = googleEntriesCache.get(cacheKey);
  if (cached && cached.expiresAt > Date.now()) {
    return cached.entries;
  }

  try {
    const response = await fetch(googleLexiconJsonUrl(voiceLocale), {
      cache: 'no-cache'
    });
    if (!response.ok) {
      return [];
    }

    const parsed = parseGoogleLexiconJson(await response.json());
    googleEntriesCache.set(cacheKey, {
      entries: parsed.entries,
      expiresAt: Date.now() + LEXICON_ENTRIES_TTL_MS
    });
    return parsed.entries;
  } catch {
    return cached?.entries ?? [];
  }
}

async function loadProviderLexicon(
  provider: AudioJitProviderId,
  voiceLocale: string
): Promise<{ revision: string | null; entries: LexiconEntry[] }> {
  const manifest = await fetchLexiconRevisionsManifest();
  const revision = getManifestRevision(manifest, provider, voiceLocale);
  if (!revision) {
    return { revision: null, entries: [] };
  }

  const entries =
    provider === 'google'
      ? await loadGoogleLexiconEntries(voiceLocale, revision)
      : await loadAzureLexiconEntries(voiceLocale, revision);

  return { revision, entries };
}

export function textHasLexiconMatch(
  text: string,
  entries: LexiconEntry[]
): boolean {
  if (!entries.length || !text.trim()) return false;
  return entries.some(entry => buildLexiconRegex(entry.match).test(text));
}

const LEXICON_PROVIDERS = new Set<AudioJitProviderId>(['google', 'azure']);

/**
 * Returns revision for cache key when provider uses lexicon and text matches.
 */
export async function resolveLexiconRevisionForCacheKey(
  provider: AudioJitProviderId | string,
  voiceLocale: string,
  text: string
): Promise<string | undefined> {
  const providerId = String(provider).toLowerCase() as AudioJitProviderId;
  if (!LEXICON_PROVIDERS.has(providerId)) {
    return undefined;
  }

  const { revision, entries } = await loadProviderLexicon(
    providerId,
    voiceLocale
  );
  if (!revision || !textHasLexiconMatch(normalizeJitCacheText(text), entries)) {
    return undefined;
  }

  return revision;
}
